import {
  applyAnimationsToObject,
  isEffectLooping,
} from '../../components/layout/gridpage/AppLeftSection/utils/animationFunctions';
import {
  resetTimelinePlaybackClock,
  getTimelinePlaybackTime,
} from '../../components/AddComponents/utils/timelinePlaybackClock';
import { resetAnimationsToOriginalState } from '../ThreeJsUtils/applyAnimations';

/**
 * Max end time across all active animation objects' timeline effects.
 */
export function getPreviewTimelineEndTime(animationObjects = []) {
  let maxEnd = 0;
  for (let i = 0; i < animationObjects.length; i++) {
    const animObj = animationObjects[i];
    if (animObj?.isActive === false) continue;

    const effects = animObj.timelineEffects;
    if (effects?.length) {
      for (let j = 0; j < effects.length; j++) {
        const end = Number(effects[j]?.endTime) || 0;
        if (end > maxEnd) maxEnd = end;
      }
    } else if (animObj.endTime !== undefined) {
      const end = Number(animObj.endTime) || 0;
      if (end > maxEnd) maxEnd = end;
    }
  }
  return maxEnd;
}

export function hasLoopingPreviewEffects(animationObjects = []) {
  return animationObjects.some((animObj) => {
    if (animObj?.isActive === false) return false;
    if (isEffectLooping(animObj)) return true;
    return (animObj.timelineEffects || []).some((effect) =>
      isEffectLooping(effect)
    );
  });
}

function resolveAnimationsList(animObj) {
  let animations = animObj.timelineEffects;
  if (!animations || animations.length === 0) {
    if (animObj.startTime !== undefined && animObj.endTime !== undefined) {
      animations = [
        {
          ...animObj,
          startTime: animObj.startTime,
          endTime: animObj.endTime,
          effectType: animObj.effectType || 'move',
          easing: animObj.easing || 'None',
          activeAxes: animObj.activeAxes || { x: true, y: false, z: false },
          startPosition: animObj.startPosition || { x: 0, y: 0, z: 0 },
          endPosition: animObj.endPosition || { x: 10, y: 0, z: 0 },
        },
      ];
    }
  }
  return animations;
}

/**
 * Starts all active timeline animations with a shared preview clock (like Design).
 * Objects that finish early hold their end pose until the longest clip completes,
 * then everything restores to the original (pre-play) state together.
 */
export function startAllSceneAnimations(
  scene,
  animationObjects,
  activeAnimationsRef,
  options = {}
) {
  if (!scene || !animationObjects?.length || !activeAnimationsRef) return;

  const { previewTimelineRafRef } = options;

  // Cancel prior object loops + master watch
  if (previewTimelineRafRef?.current) {
    cancelAnimationFrame(previewTimelineRafRef.current);
    previewTimelineRafRef.current = null;
  }
  activeAnimationsRef.current.forEach((animationIdRef) => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }
  });
  activeAnimationsRef.current.clear();

  const playEndTime = getPreviewTimelineEndTime(animationObjects);
  const keepPlayingForLoop = hasLoopingPreviewEffects(animationObjects);
  if (playEndTime <= 0) {
    if (typeof window !== 'undefined') {
      window.__timelinePlaybackActive = false;
    }
    return;
  }

  resetTimelinePlaybackClock(0, 1);
  if (typeof window !== 'undefined') {
    window.__timelinePlaybackActive = true;
  }

  for (let i = 0; i < animationObjects.length; i++) {
    const animObj = animationObjects[i];
    if (animObj.isActive === false) continue;

    const animationObject = scene.getObjectByName(animObj.id);
    if (!animationObject) continue;

    const animations = resolveAnimationsList(animObj);
    if (animations && animations.length > 0) {
      const animationIdRef = { current: null };
      applyAnimationsToObject(animationObject, animations, animationIdRef);
      activeAnimationsRef.current.set(animObj.id, animationIdRef);
    }
  }

  const finishPreviewTimeline = () => {
    if (typeof window !== 'undefined') {
      window.__timelinePlaybackActive = false;
    }
    if (previewTimelineRafRef?.current) {
      cancelAnimationFrame(previewTimelineRafRef.current);
      previewTimelineRafRef.current = null;
    }

    // Stop object loops without letting each one reset independently
    activeAnimationsRef.current.forEach((animationIdRef) => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    });
    activeAnimationsRef.current.clear();

    resetTimelinePlaybackClock(0, 1);
    resetAnimationsToOriginalState(scene, animationObjects, {
      restoreTransforms: true,
      restoreMaterials: true,
    });
  };

  const watchTimeline = () => {
    if (typeof window === 'undefined' || !window.__timelinePlaybackActive) {
      return;
    }
    const elapsed = getTimelinePlaybackTime();
    if (!keepPlayingForLoop && elapsed >= playEndTime) {
      finishPreviewTimeline();
      return;
    }
    const id = requestAnimationFrame(watchTimeline);
    if (previewTimelineRafRef) {
      previewTimelineRafRef.current = id;
    }
  };

  const watchId = requestAnimationFrame(watchTimeline);
  if (previewTimelineRafRef) {
    previewTimelineRafRef.current = watchId;
  }
}

/**
 * Stop shared preview timeline + all object animation loops.
 */
export function stopAllSceneAnimations(
  activeAnimationsRef,
  previewTimelineRafRef
) {
  if (typeof window !== 'undefined') {
    window.__timelinePlaybackActive = false;
    window.__timelinePlaybackPaused = false;
  }
  if (previewTimelineRafRef?.current) {
    cancelAnimationFrame(previewTimelineRafRef.current);
    previewTimelineRafRef.current = null;
  }
  if (!activeAnimationsRef) return;
  activeAnimationsRef.current.forEach((animationIdRef) => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
  });
  activeAnimationsRef.current.clear();
  resetTimelinePlaybackClock(0, 1);
}

let _registeredActiveAnimationsRef = null;
let _registeredPreviewTimelineRafRef = null;

/** GridPage / AppLeftPanel register refs so exit-preview can stop RAF before transform restore. */
export function registerPreviewAnimationRefs(
  activeAnimationsRef,
  previewTimelineRafRef
) {
  _registeredActiveAnimationsRef = activeAnimationsRef;
  _registeredPreviewTimelineRafRef = previewTimelineRafRef;
}

/** Stop preview timeline/animation loops without needing component refs. */
export function stopPreviewTimelinePlayback() {
  stopAllSceneAnimations(
    _registeredActiveAnimationsRef,
    _registeredPreviewTimelineRafRef
  );
}
